import {
  abTest as ownerWaveAbTest,
  TEST_CONFIG as OWNER_WAVE_CONFIG,
} from 'js/ab_tests/tests/autotest_logged_in_profile_owner_2026_q3';
import {
  abTest as ownerSmokeAbTest,
  TEST_CONFIG as OWNER_SMOKE_CONFIG,
} from 'js/ab_tests/tests/autotest_logged_in_profile_owner_smoke';
import {
  abTest as visitorWaveAbTest,
  TEST_CONFIG as VISITOR_WAVE_CONFIG,
} from 'js/ab_tests/tests/autotest_logged_in_profile_visitor_2026_q3_v2';
import { currentUser } from 'js/utils/user';

import { ARMS } from './armRegistry';
import { viewedUserId } from './profileFacts';

import type { AnyBucket, Arm, ArmSpec, Mode } from './armRegistry';
import type { AutotestStamp } from './tracking';

export const WAVE_TEST_NAMES: Record<Mode, string> = {
  visitor: 'autotest_logged_in_profile_visitor_2026_q3_v2',
  owner: 'autotest_logged_in_profile_owner_2026_q3',
};

const KEYS: Record<
  Mode,
  ReadonlyArray<{
    testName: string;
    // Optional because the generator .compacts the js_config, so a yml with no
    // active: key produces a module with no active property. Retiring the
    // owner smoke at launch must not need a code change to typecheck.
    config: { active?: boolean };
    abTest: () => AnyBucket | 'holdout';
  }>
> = {
  visitor: [
    {
      testName: WAVE_TEST_NAMES.visitor,
      config: VISITOR_WAVE_CONFIG,
      abTest: visitorWaveAbTest,
    },
  ],
  owner: [
    {
      testName: WAVE_TEST_NAMES.owner,
      config: OWNER_WAVE_CONFIG,
      abTest: ownerWaveAbTest,
    },
    {
      testName: 'autotest_logged_in_profile_owner_smoke',
      config: OWNER_SMOKE_CONFIG,
      abTest: ownerSmokeAbTest,
    },
  ],
};

export type ProfileAutotestResolution = {
  testName: string;
  mode: Mode;
  bucket: AnyBucket;
  spec: ArmSpec | undefined;
  stamp: AutotestStamp;
};

// Neither yml declares a holdout, so 'holdout' is unreachable; the framework
// return type still includes it, and control is its only safe reading.
function normalizeBucket(bucket: AnyBucket | 'holdout'): AnyBucket {
  return bucket === 'holdout' ? 'control' : bucket;
}

// Which half of the cohort this pageview belongs to. Classifies the pageview,
// not the user: someone who reads other profiles and also visits their own
// enrols in both tests, so treat the two as overlapping populations in analysis.
//
// The viewer gate mirrors Ruby's logged_in? on its unconfirmed? term only, not
// on the hidden_spammer? half of User#hidden?. A confirmed hidden spammer is
// therefore bucketed by this test and by the live logged-out profile autotest
// both, on one pageview under one auvid — a known, accepted overlap.
export function resolveMode(): Mode | null {
  const viewedId = viewedUserId();

  if (currentUser == null || currentUser.unconfirmed || viewedId == null) {
    return null;
  }

  return currentUser.id === viewedId ? 'owner' : 'visitor';
}

// The wave key outranks the smoke key — a cascade only the owner half still
// has; the visitor wave-2 ships without a smoke, so its list is the wave key
// alone. A key drives dispatch when it is active OR, for the wave only, its
// abTest() resolved off-control (a ?ab_test[key]=arm override or a prior
// bucketing). With every key inactive and no override this returns null and the
// harness is a pure passthrough. Calling abTest() here is the sole membership
// recording trigger — it records only while that key is active, and
// sessionStorage dedupes repeats.
//
// The off-control fallthrough is wave-only on purpose, and it is what makes the
// inactive visitor key QA-able by URL override. A smoke bucket has no arm to
// dispatch, so forcing one is meaningless, while a finished smoke resolved to
// anything but control would otherwise be returned on every pageview and stamp
// every click with a bucket and no test name.
export function resolveProfileAutotest(): ProfileAutotestResolution | null {
  const mode = resolveMode();
  if (mode == null) return null;

  for (const key of KEYS[mode]) {
    const active = key.config.active === true;
    const isWave = key.testName === WAVE_TEST_NAMES[mode];
    const bucket = normalizeBucket(key.abTest());
    if (!active && !(isWave && bucket !== 'control')) continue;

    return {
      testName: key.testName,
      mode,
      bucket,
      // Smoke buckets never reach the ARMS table: the smoke key keeps its
      // launch-time control render even after the arm PRs fill the registry,
      // because smoke stays active until wave activation.
      spec: isWave && bucket !== 'control' ? ARMS[bucket as Arm] : undefined,
      stamp: {
        ...(active && { autotest: key.testName }),
        autotest_bucket: bucket,
        mode,
      },
    };
  }

  return null;
}
